import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useSnackbar } from 'notistack';
import fileDownload from 'js-file-download';
import { Export as ExportIcon } from '../../../assets/icons/export';
import PositionBulkActions from './PositionBulkActions';
import PermissionChecker from '../../../components/common/PermissionChecker';
import { useExportPositionsMutation, useExportPositionsWithCompaFieldsMutation } from '../../../api/download';
import { hasPermission } from '../../../utils/permission';
import FilterDialog from '../../../components/common/FilterButton/FilterDialog';

const PositionTableActions = (props) => {
    const { filters, initialFilters, onFiltersApply, onFiltersClear, selectedPositions, filterProperties, action } =
        props;

    const [openFilterDialog, setOpenFilterDialog] = useState(false);

    const { enqueueSnackbar } = useSnackbar();

    const authorities = useSelector((state) => state.session.user.authorities);
    const userHasCompaExportPermisson = hasPermission(authorities, [
        'ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPA_EXPORT_POSITIONS_DATA',
    ]);

    const [exportPositions, { isLoading: isDownloading }] = useExportPositionsMutation();
    const [exportPositionsWithCompaFields, { isLoading: isDownloadingWithCompaFields }] =
        useExportPositionsWithCompaFieldsMutation();

    const downloadPositionData = async () => {
        try {
            const exportApi = userHasCompaExportPermisson ? exportPositionsWithCompaFields : exportPositions;

            const response = await exportApi().unwrap();
            if (!response) return;
            if (response.status === 'ERROR') throw new Error(response?.message);

            fileDownload(new Blob([response]), 'positions.xlsx');
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Download failed', { variant: 'error' });
        }
    };

    return (
        <>
            <Grid container spacing={1} direction="row-reverse" justifyContent="flex-start" alignItems="center" mb={2}>
                {selectedPositions.length > 1 && (
                    <Grid item alignContent="flex-end" xs="auto">
                        <PositionBulkActions selectedPositions={selectedPositions} handleAction={action} />
                    </Grid>
                )}
                <PermissionChecker
                    requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_SEARCH_POSITIONS']}
                    noAuthRedirect={false}
                >
                    <Grid item alignContent="flex-end" xs="auto">
                        <Button
                            color="primary"
                            disabled={false}
                            onClick={() => setOpenFilterDialog(true)}
                            startIcon={<FilterAltIcon />}
                            size="medium"
                            variant={filters.length ? 'contained' : 'text'}
                        >
                            Filter
                        </Button>
                        <FilterDialog
                            onApply={onFiltersApply}
                            onClear={onFiltersClear}
                            onClose={() => setOpenFilterDialog(false)}
                            open={openFilterDialog}
                            operators={[]}
                            properties={filterProperties}
                            initialFilters={initialFilters}
                        />
                    </Grid>
                </PermissionChecker>
                <PermissionChecker
                    requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_EXPORT_POSITIONS_DATA']}
                    noAuthRedirect={false}
                >
                    <Grid item alignContent="flex-end" xs="auto">
                        <LoadingButton
                            loading={isDownloading || isDownloadingWithCompaFields}
                            color="secondary"
                            disabled={false}
                            onClick={downloadPositionData}
                            size="medium"
                            variant="text"
                            startIcon={<ExportIcon fontSize="small" />}
                        >
                            Export
                        </LoadingButton>
                    </Grid>
                </PermissionChecker>
            </Grid>
        </>
    );
};

export default PositionTableActions;
